import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';

// Lazy load de páginas
const Login = lazy(() => import('../pages/Authentication/LoginV2'));
const Register = lazy(() => import('../pages/Authentication/Register'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Companies = lazy(() => import('../pages/Admin/Companies'));
const Users = lazy(() => import('../pages/Users'));
const Products = lazy(() => import('../pages/Products'));
const Sales = lazy(() => import('../pages/Sales'));
const PhoneNumbers = lazy(() => import('../pages/PhoneNumbers'));
const Assistants = lazy(() => import('../pages/Assistants'));
const Automations = lazy(() => import('../pages/Automations'));
const SetupWizard = lazy(() => import('../pages/SetupWizard'));
const WhatsApp = lazy(() => import('../pages/WhatsApp'));
const WhatsAppTemplates = lazy(() => import('../pages/WhatsApp/Templates'));
const MultiAgentWhatsApp = lazy(() => import('../pages/WhatsApp/MultiAgentWhatsApp'));
const PricingPlans = lazy(() => import('../pages/Pricing/PricingPlans'));
const SubscriptionManagement = lazy(() => import('../pages/Subscriptions/SubscriptionManagement'));
const CompanyDashboard = lazy(() => import('../pages/Admin/CompanyDashboard'));
const EmployeeManagement = lazy(() => import('../pages/Admin/EmployeeManagement'));
const SystemConfig = lazy(() => import('../pages/Admin/SystemConfig'));
const ConfigDatabase = lazy(() => import('../pages/Admin/ConfigDatabase'));
const ConfigTwilio = lazy(() => import('../pages/Admin/ConfigTwilio'));
const ConfigAI = lazy(() => import('../pages/Admin/ConfigAI'));
const ConfigAutomation = lazy(() => import('../pages/Admin/ConfigAutomation'));

// Componente de carga
const Loading = () => (
  <div className="d-flex justify-content-center align-items-center min-vh-100">
    <Spinner animation="border" variant="primary" />
  </div>
);

// Componente de ruta protegida
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode;
  requiredPermission?: string;
  requiredUserType?: string;
}> = ({ children, requiredPermission, requiredUserType }) => {
  const { isAuthenticated, hasPermission, user } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (requiredUserType && user?.type !== requiredUserType) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

// Componente de ruta pública
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Rutas públicas */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } 
        />
        
        {/* Rutas protegidas con layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          
          {/* Rutas de Super Admin */}
          <Route 
            path="/admin/companies" 
            element={
              <ProtectedRoute requiredUserType="super_admin">
                <Companies />
              </ProtectedRoute>
            } 
          />
          
          {/* Rutas de administración */}
          <Route 
            path="/users" 
            element={
              <ProtectedRoute requiredPermission="users.view">
                <Users />
              </ProtectedRoute>
            } 
          />
          
          {/* Otras rutas */}
          <Route path="/products" element={<Products />} />
          <Route path="/sales" element={<Sales />} />
          <Route path="/phone-numbers" element={<PhoneNumbers />} />
          <Route path="/assistants" element={<Assistants />} />
          <Route path="/automations" element={<Automations />} />
          <Route path="/setup-wizard" element={<SetupWizard />} />
          <Route path="/whatsapp" element={<WhatsApp />} />
          <Route path="/whatsapp/templates" element={<WhatsAppTemplates />} />
          <Route path="/whatsapp/multi-agent" element={<MultiAgentWhatsApp />} />
          <Route path="/pricing" element={<PricingPlans />} />
          <Route path="/subscriptions" element={<SubscriptionManagement />} />
          <Route path="/admin/company" element={<CompanyDashboard />} />
          <Route path="/admin/employees" element={<EmployeeManagement />} />
          <Route path="/admin/config" element={<SystemConfig />} />
          <Route path="/admin/config/database" element={<ConfigDatabase />} />
          <Route path="/admin/config/twilio" element={<ConfigTwilio />} />
          <Route path="/admin/config/ai" element={<ConfigAI />} />
          <Route path="/admin/config/automation" element={<ConfigAutomation />} />
        </Route>
        
        {/* Redirección por defecto */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;